import { createClient, type Session } from "@supabase/supabase-js";

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

/** Requested once at sign-in; Docs and Calendar are then usable without a second consent. */
const GOOGLE_SCOPES = [
  "https://www.googleapis.com/auth/drive.file",
  "https://www.googleapis.com/auth/calendar.events.owned"
];

export const supabase =
  url && anonKey
    ? createClient(url, anonKey, {
        auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true }
      })
    : undefined;

export function isSupabaseBrowserConfigured(): boolean {
  return Boolean(supabase);
}

export async function currentSession(): Promise<Session | null> {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data.session;
}

/**
 * Rewrites the stored session without the Google provider tokens. They are only
 * needed long enough to be verified and encrypted server-side; keeping them in
 * browser storage afterwards would leave a usable Google credential on the
 * device.
 */
export async function scrubGoogleProviderTokens(session: Session): Promise<void> {
  if (!supabase || !session.provider_token) return;
  const { error } = await supabase.auth.setSession({
    access_token: session.access_token,
    refresh_token: session.refresh_token
  });
  if (error) throw error;
}

export async function signInWithGoogle(): Promise<void> {
  if (!supabase) {
    throw new Error("Supabase n’est pas configuré.");
  }
  const { error } = await supabase.auth.signInWithOAuth({
    provider: "google",
    options: {
      redirectTo: window.location.origin,
      scopes: GOOGLE_SCOPES.join(" "),
      queryParams: {
        // `offline` + `consent` are what make Google return a refresh token,
        // without which Docs and Calendar stop working after one hour.
        access_type: "offline",
        prompt: "consent select_account",
        include_granted_scopes: "true"
      }
    }
  });
  if (error) throw error;
}

export async function signOut(): Promise<void> {
  await supabase?.auth.signOut();
}
